/**
 * Created by Ning Jiang on 1/14/2020.
 */

import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {
    ISymbolSizeConfig,
    ISymbolStateConfig,
    SymbolStateTransition
} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Power2, TimelineLite, TweenLite} from "gsap";
import {DeadwoodGameConfig} from "../DeadwoodGameConfig";
import {DeadwoodSoundConfig} from "../DeadwoodSoundConfig";
import {DeadwoodReelSymbol} from "./DeadwoodReelSymbol";

export class DeadwoodBonusSymbol extends DeadwoodReelSymbol {

    private _landingSprite:PIXI.Sprite;
    private _landingSpine:PIXI.spine.Spine;
    private _landingAddSprite:PIXI.Sprite;
    private _landingAddSpine:PIXI.spine.Spine;
    private _retriggerSprite:PIXI.Sprite;
    private _attention:TimelineSprite;

    constructor(symName:string, reelId:number, state?:ISymbolStateConfig, size?:ISymbolSizeConfig) {
        super(symName, reelId, state, size);
    }

    protected initAnimations():void {
        this._landingSprite = new PIXI.Sprite();
        this._landingSprite.anchor.set(0.5);
        this._landingSprite.alpha = 0;
        this.addChild(this._landingSprite);

        this._landingSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("B_rotate"));
        this._landingSpine.autoUpdate = true;
        this._landingSprite.addChild(this._landingSpine);

        this._landingAddSprite = new PIXI.Sprite();
        this._landingAddSprite.anchor.set(0.5);
        this._landingAddSprite.alpha = 0;
        this.addChild(this._landingAddSprite);

        this._landingAddSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("B_rotate_add"));
        this._landingAddSpine.autoUpdate = true;
        this._landingAddSprite.addChild(this._landingAddSpine);
    }

    protected initState(stateConfig:ISymbolStateConfig):boolean {
        const result:boolean = super.initState(stateConfig);

        if(this._retriggerSprite && this._retriggerSprite.parent) {
            this.addChild(this._retriggerSprite);
        }

        return result;
    }

    protected addNewStateAnimation(timeline:gsap.TimelineLite, newState:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        if(this.reelId === 4) {
            Logger.logDev(`B change to new state ${newState.state.keyword}`);
        }
        super.addNewStateAnimation(timeline, newState, stateAnimation, delay);

        if(newState.state.keyword === DeadwoodGameConfig.instance.SYMBOL_STATES.landing.keyword) {
            this._landingSpine.state.clearTrack(0);
            this._landingSpine.skeleton.setToSetupPose();
            if(!this._attention) {
                this._attention = new TimelineSprite(GameResources.getTextures("bonusAttention"));
                this._attention.anchor.set(0.5);
                this._attention.blendMode = PIXI.BLEND_MODES.ADD;
                this._attention.hide();
            }

            const landingTl:TimelineLite = new TimelineLite();
            landingTl.add([
                () => {
                    this.addChild(this._landingSprite);
                    this.addChild(this._attention);
                    this._attention.show();
                    this._attention.playLoop();
                    if(this._retriggerSprite && this._retriggerSprite.parent) {
                        this.addChild(this._retriggerSprite);
                    }
                    this.addChild(this._landingAddSprite);
                    this._landingAddSpine.state.setAnimation(0, "animation", false);
                },
                TweenLite.to(this._landingSprite, 0.1, {alpha : 1}),
                TweenLite.to(this._landingAddSprite, 0.1, {alpha : 1}),
                new SpineTween(this._landingSpine, 0, "animation"),
            ]);

            timeline.add(landingTl, delay);
        } else if(newState.state.keyword === DeadwoodGameConfig.instance.SYMBOL_STATES.win.keyword
            || newState.state.keyword === DeadwoodGameConfig.instance.SYMBOL_STATES.broken.keyword) {
            this._landingAddSpine.state.clearTrack(0);
            this._landingAddSpine.skeleton.setToSetupPose();
            const winTl:TimelineLite = new TimelineLite();
            winTl.add([
                () => {
                    if(this._attention) {
                        this._attention.stopLoop();
                        this._attention.hide();
                    }
                    this.addChild(this._landingAddSprite);
                    new SpineTween(this._landingAddSpine, 0, "animation", -1);
                },
                TweenLite.to(this._landingAddSprite, 0.1, {alpha : 1}),
                TweenLite.to(this._landingSprite, 0.1, {
                    alpha : 0,
                    onComplete : () => this._landingSpine.state.setEmptyAnimation(0, 0)
                })
            ]);

            timeline.add(winTl, delay);
        } else {
            timeline.add([
                () => {
                    if(this._attention) {
                        this._attention.stopLoop();
                        this._attention.hide();
                    }
                },
                TweenLite.to(this._landingSprite, 0.1, {
                    alpha : 0,
                    onComplete : () => {
                        if(this._landingSpine && this._landingSpine.state) {
                            this._landingSpine.state.setEmptyAnimation(0, 0);
                        }
                    }
                }),
                TweenLite.to(this._landingAddSprite, 0.1, {
                    alpha : 0,
                    onComplete : () => {
                        if(this._landingAddSpine && this._landingAddSpine.state) {
                            this._landingAddSpine.state.setEmptyAnimation(0, 0);
                        }
                    }
                })
            ], delay);
        }
    }

    public playLowSymbolLandAttention(stay:boolean):void {
        this._landingAddSpine.state.clearTrack(0);
        this._landingAddSpine.skeleton.setToSetupPose();
        this.addChild(this._landingAddSprite);
        this._landingAddSpine.state.setAnimation(0, "animation", false);
        TweenLite.fromTo(this._landingAddSprite.scale, 0.3,
            {x : 1, y : 1},
            {
                x : 1.25, y : 1.25,
                onComplete : () => TweenLite.to(this._landingAddSprite.scale, 0.2, {x : 1, y : 1})
            });
        TweenLite.fromTo(this._landingAddSprite, 0.3,
            {alpha : 0},
            {
                alpha : 1,
                onComplete : () => {
                    if(!stay) {
                        TweenLite.to(this._landingAddSprite, 0.2, {
                            alpha : 0,
                            onComplete : () => this._landingAddSpine.state.setEmptyAnimation(0, 0)
                        });
                    }
                }
            });
    }

    public getRetriggerTimeline():TimelineLite {
        if(!this._retriggerSprite) {
            this._retriggerSprite = new PIXI.Sprite(GameResources.getTextures("fsRetrigger")[0]);
            this._retriggerSprite.anchor.set(0.5);
        }

        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            TweenLite.fromTo(this._retriggerSprite, 0.2, {alpha : 0}, {alpha : 1}),
            TweenLite.to(this._retriggerSprite.scale, 1.2, {x : 1.2, y : 1.2, ease : Power2.easeOut}),
            () => {
                this.addChild(this._retriggerSprite);
                SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.PLUS_SPIN);
            }
        ]);
        timeline.add([
            TweenLite.to(this._retriggerSprite, 0.2, {alpha : 0}),
            TweenLite.to(this._retriggerSprite, 0.2, {y : -50})
        ], '-=0.2');
        timeline.add(() => {
            this.removeChild(this._retriggerSprite);
        });
        return timeline;
    }

    protected reset(remove:boolean):void {
        super.reset(remove);

        if(remove) {
            if(this._attention) {
                this.removeChild(this._attention);
                this._attention.destroy();
            }
            if(this._retriggerSprite) {
                if(this._retriggerSprite.parent) {
                    this.removeChild(this._retriggerSprite);
                }
                this._retriggerSprite.destroy();
            }

            if(this._landingSprite) {
                if(this._landingSprite.parent) {
                    this.removeChild(this._landingSprite);
                }
                this._landingSprite.removeChild(this._landingSpine);
                this._landingSpine.destroy();
                this._landingSprite.destroy();
            }

            if(this._landingAddSprite) {
                if(this._landingAddSprite.parent) {
                    this.removeChild(this._landingAddSprite);
                }
                this._landingAddSprite.removeChild(this._landingAddSpine);
                this._landingAddSpine.destroy();
                this._landingAddSprite.destroy();
            }
        }
    }
}